import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpenIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { MANUALS, MANUAL_STATUS, getManualFileUrl, getGroupedManuals } from '../config/manuals';

/**
 * 매뉴얼 목록 화면.
 * 새 매뉴얼은 src/config/manuals.js 에만 추가하면 이 화면에 자동으로 나온다.
 */
const ManualCenter = () => {
  const navigate = useNavigate();

  const readyCount = MANUALS.filter((m) => m.status === MANUAL_STATUS.READY).length;
  const groups = getGroupedManuals();

  const renderCard = (manual) => {
    const isReady = manual.status === MANUAL_STATUS.READY;

    return (
      <div
        key={manual.id}
        className={`relative rounded-xl bg-gray-50 p-5 transition-colors ${
          isReady
            ? 'hover:bg-gray-100 cursor-pointer'
            : 'opacity-70'
        }`}
        role={isReady ? 'button' : undefined}
        tabIndex={isReady ? 0 : -1}
        onClick={isReady ? () => navigate(`/manuals/${manual.id}`) : undefined}
        onKeyDown={(e) => {
          if (!isReady) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(`/manuals/${manual.id}`);
          }
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={isReady ? 'text-primary-600' : 'text-gray-300'}>
              <BookOpenIcon className="w-6 h-6" />
            </div>
            <div
              className={`text-base font-semibold truncate ${
                isReady ? 'text-gray-900' : 'text-gray-400'
              }`}
            >
              {manual.title}
            </div>
          </div>

          {!isReady && (
            <span className="shrink-0 inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">
              준비 중
            </span>
          )}
        </div>

        <p
          className={`mt-3 text-sm leading-relaxed ${
            isReady ? 'text-gray-600' : 'text-gray-400'
          }`}
        >
          {manual.summary}
        </p>

        {isReady && Array.isArray(manual.chapters) && manual.chapters.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {manual.chapters.slice(0, 4).map((chapter) => (
              <li
                key={chapter}
                className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-600"
              >
                {chapter}
              </li>
            ))}
            {manual.chapters.length > 4 && (
              <li className="inline-flex items-center px-1 text-[11px] text-gray-400">
                외 {manual.chapters.length - 4}개
              </li>
            )}
          </ul>
        )}

        {isReady && (
          <div className="mt-4 flex items-center justify-between">
            <span className="text-[11px] text-gray-400">
              {manual.updatedAt ? `${manual.updatedAt} 기준` : ''}
            </span>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600"
              onClick={(e) => {
                e.stopPropagation();
                window.open(getManualFileUrl(manual.id), '_blank', 'noopener');
              }}
              title="새 탭에서 열기"
            >
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              새 탭
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 space-y-4">
      <div className="rounded-2xl bg-white p-5">
        <h2 className="text-[17px] font-bold text-gray-900">매뉴얼</h2>
        <p className="mt-1 text-sm text-gray-500">
          메뉴별 사용 안내입니다. 준비된 매뉴얼 {readyCount}건.
        </p>
      </div>

      {groups.map(({ group, items }) => (
        <section key={group} className="rounded-2xl bg-white p-5 space-y-4">
          <div className="flex items-center gap-2">
            <span className="inline-block w-1 h-4 bg-primary-600 rounded-sm" />
            <h3 className="text-[17px] font-bold text-gray-900">{group}</h3>
            <span className="text-xs text-gray-400">{items.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map(renderCard)}
          </div>
        </section>
      ))}
    </div>
  );
};

export default ManualCenter;
